import { HTMLAttributes, useState, useEffect } from "react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import {
  installFlowAssetType,
  installFlowTechniquePhoto,
  installFlowOrganizationId,
  installFlowLocationId,
  installFlowSublocationId,
  installFlowSensorKitSerial,
  installFlowScanId,
  installFlowInstallationId,
} from "@/installFlow/storage-keys";
import { useNavigate } from "@tanstack/react-router";
import { Route } from "@/routes/installFlow/physical-install-steps";
import { AlertTriangle, ChevronLeft } from "lucide-react";
import { apiClient } from "@/api/client";
import ThaloIcon from "@/assets/thalo_icon.svg";
import Markdown from "react-markdown";
import { useInstallFlowSteps } from "@/installFlow/api";

interface AuthProps extends HTMLAttributes<HTMLDivElement> {}

export function PhysicalInstallSteps({ className }: AuthProps) {
  const { step: currentStep } = Route.useSearch();
  const [technique, setTechnique] = useState<string>();
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Read synchronously so the query is enabled on the first render.
  // Falls back to "default" if no asset type was stored — the backend
  // will serve the default flow in that case.
  const equipmentType =
    typeof sessionStorage !== "undefined"
      ? (installFlowAssetType.get() ?? "default")
      : "default";

  useEffect(() => {
    if (typeof sessionStorage === "undefined") return;
    const stored = installFlowTechniquePhoto.get();
    if (stored) setTechnique(stored);
  }, []);

  const { data, isPending } = useInstallFlowSteps(equipmentType);
  const steps = data?.results ?? [];
  const totalSteps = steps.length;
  const step = steps[currentStep];
  // Pre-fetch the next step's image into the browser cache so it
  // appears instantly when the user taps "Next".
  const nextStepImage = steps[currentStep + 1]?.image;
  useEffect(() => {
    if (!nextStepImage) return;
    const img = new Image();
    img.src = nextStepImage;
  }, [nextStepImage]);

  const setStep = (step: number) => {
    navigate({
      search: (prev) => ({ ...prev, step }),
    });
  };

  const handleBack = () => {
    if (currentStep > 0) setStep(currentStep - 1);
  };

  const handleContinue = async () => {
    if (currentStep < totalSteps - 1) {
      setStep(currentStep + 1);
      return;
    }

    setIsLinking(true);
    setLinkError(null);
    try {
      const organizationId = installFlowOrganizationId.get();
      const locationId = installFlowLocationId.get();
      const sublocationId = installFlowSublocationId.get();
      const serialNumber = installFlowSensorKitSerial.get() ?? "";
      const scanId = installFlowScanId.get() ?? "";
      const installationId = installFlowInstallationId.get() ?? "";

      await apiClient.post(
        `organizations/${organizationId}/locations/${locationId}/sublocations/${sublocationId}/installations/${installationId}/link-sensor-kit/`,
        { scan_id: scanId, sensor_kit_serial_number: serialNumber },
      );

      await navigate({ to: "/installFlow/live-data" });
    } catch {
      setLinkError("Failed to link the sensor. Please try again.");
      setIsLinking(false);
    }
  };

  if (isLinking) {
    return (
      <div className="w-full max-w-[460px] min-h-screen flex flex-col items-center justify-center gap-6 p-6 bg-white">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-32 h-32 rounded-full bg-blue-100 animate-ping opacity-30" />
          <div className="absolute w-24 h-24 rounded-full bg-blue-200 animate-ping opacity-40 [animation-delay:150ms]" />
          <img src={ThaloIcon} alt="Thalo" className="relative w-16 h-16 animate-bounce z-10" />
        </div>
        <p className="text-lg font-bold text-black">Linking sensor kit...</p>
        <p className="text-sm text-gray-500 text-center">This may take a few seconds.</p>
      </div>
    );
  }

  if (isPending || !step) {
    return (
      <div className="w-full max-w-[460px] min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <img src={ThaloIcon} alt="Thalo" className="w-24 h-24 animate-pulse" />
      </div>
    );
  }

  return (
    <div className={cn("p-6 w-full max-w-[500px] flex flex-col", className)}>
      <div className="h-[720px] flex flex-col">

        {/* Progress Indicator */}
        <div className="w-full mb-6 flex items-center gap-3">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 0}
            className={cn(
              "flex-shrink-0 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center transition-opacity",
              currentStep === 0 ? "opacity-30 cursor-not-allowed" : "hover:bg-gray-100",
            )}
          >
            <ChevronLeft className="w-4 h-4 text-gray-700" />
          </button>

          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0053EE] rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>

          <span className="flex-shrink-0 text-sm font-medium text-gray-500 tabular-nums">
            {currentStep + 1}/{totalSteps}
          </span>
        </div>

        {/* Step Content */}
        <div className="flex flex-col gap-6 flex-1">
          <div className="w-full rounded-lg flex items-center justify-center">
            {step.image ? (
              <img src={step.image} alt={step.title} className="h-[252px] object-cover" />
            ) : (
              <img src={technique} alt="asset-photo" className="h-[252px] object-cover" />
            )}
          </div>

          <h2 className="text-xl font-bold text-black leading-[20px] mt-1">
            {step.title}
          </h2>

          <div className="flex flex-col gap-1 text-sm font-medium text-black leading-[20px]">
            <Markdown
              components={{
                ul: ({ ...props }) => <ul className="flex flex-col gap-1 ml-5" {...props} />,
                li: ({ ...props }) => <li className="list-inside list-disc" {...props} />,
              }}
            >
              {step.body}
            </Markdown>
          </div>

          {step.alert_callout && (
            <div className="bg-[#FFF3E8] rounded-lg px-4 py-3 text-sm font-medium text-[#B45309] flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <Markdown
                components={{
                  p: ({ ...props }) => <p className="m-0" {...props} />,
                }}
              >
                {step.alert_callout}
              </Markdown>
            </div>
          )}
          <div className="flex items-center justify-end w-full">
          <button
            onClick={() => console.log("Having trouble with step:", step.order)}
            className="w-28 h-12 rounded-full border border-gray-300 bg-white text-gray-500 text-base font-medium hover:bg-gray-50 transition-colors px-2 py-3 hidden"
          >
            Need help?
          </button>
          <Button
            onClick={handleContinue}
            variant="installFlow"
            className="w-[120px] h-12 rounded-[25px] font-medium px-3 py-6"
          >
            {currentStep === totalSteps - 1 ? "Complete" : "Next"}
          </Button>
          </div>
         
        </div>

        {linkError && (
          <p className="text-sm font-medium text-destructive text-center">{linkError}</p>
        )}

     
      </div>
    </div>
  );
}
